import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef, useMemo } from "react";
import { AgentProject, ChatMessage, ChatSession, ChatSessionsState } from "@/types/aiAgent";
import { toast } from "sonner";
import { createSafeLocalStorage } from "@/lib/storage";
import { chatHistoryService } from "@/services/chatHistoryService";
import { artifactService } from "@/services/artifactService";
import { chatProjectsService } from "@/services/chatProjectsService";
import {
    CHAT_HISTORY_ACTIVE_SESSION_KEY,
    generateSessionTitle,
    loadLocalChatSessions,
    migrateLocalChatSessionsToBackend,
} from "@/lib/chatHistoryMigration";
import { getApiInstantSortParts, normalizeApiInstantString } from "@/lib/dateTime";
import { randomUUID } from "@/lib/randomUUID";

const MAX_MESSAGES_PER_SESSION = 100;

interface ChatHistoryContextType {
    messages: ChatMessage[];
    activeSessionId: string | null;
    activeSession: ChatSession | null;
    sessions: Record<string, ChatSession>;
    sortedSessions: ChatSession[];
    projects: Record<string, AgentProject>;
    sortedProjects: AgentProject[];
    addMessage: (message: Omit<ChatMessage, "id" | "timestamp">, sessionId?: string) => string;
    updateMessage: (messageId: string, updates: Partial<ChatMessage>) => void;
    upsertMessageLocal: (sessionId: string, message: ChatMessage) => void;
    createSession: (firstMessage?: string, options?: { projectId?: string | null }) => string;
    createPersistedSession: (options?: { projectId?: string | null; title?: string }) => Promise<string>;
    ensureSessionPersisted: (sessionId: string, title: string, projectId: string | null) => Promise<void>;
    switchSession: (sessionId: string) => void;
    renameSession: (sessionId: string, title: string) => void;
    deleteSession: (sessionId: string) => void;
    clearCurrentSession: () => void;
    importSession: (importedMessages: Omit<ChatMessage, "id" | "timestamp">[]) => void;
    refreshSession: (sessionId: string) => Promise<void>;
    clearSessionRecommendations: (sessionId: string) => void;
    createProject: (name: string) => Promise<AgentProject>;
    renameProject: (projectId: string, name: string) => Promise<void>;
    deleteProject: (projectId: string) => Promise<void>;
    moveSessionToProject: (sessionId: string, projectId: string | null) => Promise<void>;
    isLoaded: boolean;
    isActiveSessionLoading: boolean;
    isMigrating: boolean;
    migrationMessage: string | null;
}

const ChatHistoryContext = createContext<ChatHistoryContextType | undefined>(undefined);

export const ChatHistoryProvider = ({ children }: { children: ReactNode }) => {
    const [sessions, setSessions] = useState<Record<string, ChatSession>>({});
    const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
    const [projects, setProjects] = useState<Record<string, AgentProject>>({});
    const [isLoaded, setIsLoaded] = useState(false);
    const [isActiveSessionLoading, setIsActiveSessionLoading] = useState(false);
    const [isMigrating, setIsMigrating] = useState(false);
    const [migrationMessage, setMigrationMessage] = useState<string | null>(null);
    const safeLocalStorage = React.useMemo(() => createSafeLocalStorage(() => { }), []);
    const loadingSessionIdRef = useRef<string | null>(null);
    const pendingSessionCreatesRef = useRef<Map<string, Promise<any>>>(new Map());

    const getMessageSortTime = (message: any) => {
        const raw = message?.timestamp ?? message?.created_at ?? message?.createdAt ?? message?.updated_at ?? message?.updatedAt;
        return getApiInstantSortParts(raw);
    };

    const getMessageCreatedTime = (message: any) => {
        const raw = message?.created_at ?? message?.createdAt;
        return getApiInstantSortParts(raw);
    };

    const sortMessagesChronologically = (messagesList: any[]) => (
        [...messagesList]
            .map((message, index) => ({ message, index }))
            .sort((a, b) => {
                const aTime = getMessageSortTime(a.message);
                const bTime = getMessageSortTime(b.message);
                if (aTime.epochMs !== bTime.epochMs) return aTime.epochMs - bTime.epochMs;
                if (aTime.subMs !== bTime.subMs) return aTime.subMs - bTime.subMs;
                const aCreated = getMessageCreatedTime(a.message);
                const bCreated = getMessageCreatedTime(b.message);
                if (aCreated.epochMs !== bCreated.epochMs) return aCreated.epochMs - bCreated.epochMs;
                if (aCreated.subMs !== bCreated.subMs) return aCreated.subMs - bCreated.subMs;
                return a.index - b.index;
            })
            .map((entry) => entry.message)
    );

    useEffect(() => {
        if (!isMigrating) return;

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = "Updating the Hub. Please keep this page open.";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isMigrating]);

    const mapMessageFromApi = (message: any): ChatMessage => ({
        id: message.id,
        role: message.role,
        content: message.content,
        // Falling straight through to now would sort a message with an
        // unreadable timestamp to the bottom of the conversation.
        timestamp:
            normalizeApiInstantString(message.timestamp) ??
            normalizeApiInstantString(message.created_at) ??
            normalizeApiInstantString(message.createdAt) ??
            new Date().toISOString(),
        sources: message.sources ?? undefined,
        citations: message.citations ?? undefined,
        toolCalls: message.tool_calls ?? undefined,
        toolInvocations: message.tool_invocations ?? undefined,
        contentBlocks: message.content_blocks ?? undefined,
        recommendations: message.recommendations ?? undefined,
        rating: message.rating ?? undefined,
        metadata: message.metadata ?? message.message_metadata ?? undefined,
        artifactId: message.artifact_id ?? undefined,
        artifactVersionId: message.artifact_version_id ?? undefined,
    });

    const mapSessionFromApi = (session: any, messagesLoaded = true): ChatSession => ({
        id: session.id,
        title: session.title,
        projectId: session.project_id ?? null,
        topics: session.topics ?? null,
        metadataGeneratedAt: session.metadata_generated_at ?? null,
        messages: messagesLoaded && Array.isArray(session.messages)
            ? sortMessagesChronologically(session.messages).map(mapMessageFromApi)
            : [],
        createdAt: normalizeApiInstantString(session.created_at) ?? new Date().toISOString(),
        lastUpdated: normalizeApiInstantString(session.updated_at) ?? new Date().toISOString(),
        messagesLoaded,
        isPersisted: true,
    });

    const mapProjectFromApi = useCallback((project: any): AgentProject => ({
        id: project.id,
        name: project.name,
        createdAt: normalizeApiInstantString(project.created_at) ?? new Date().toISOString(),
        updatedAt: normalizeApiInstantString(project.updated_at) ?? new Date().toISOString(),
    }), []);

    const mergeMessageRecord = (a: ChatMessage, b: ChatMessage): ChatMessage => {
        const aStreaming = (a?.metadata as any)?.status === "streaming";
        const bStreaming = (b?.metadata as any)?.status === "streaming";
        const mergeMetadata = (
            primary: ChatMessage["metadata"],
            secondary: ChatMessage["metadata"],
        ) => {
            if (!primary) return secondary;
            if (!secondary) return primary;
            if (
                typeof primary === "object"
                && primary !== null
                && typeof secondary === "object"
                && secondary !== null
            ) {
                return { ...secondary, ...primary };
            }
            return primary;
        };

        // Prefer non-streaming messages when one side is still marked streaming.
        if (aStreaming && !bStreaming) {
            return {
                ...a,
                ...b,
                // Preserve any client-only fields when the server doesn't have them yet.
                sources: b.sources ?? a.sources,
                citations: b.citations ?? a.citations,
                toolCalls: b.toolCalls ?? a.toolCalls,
                toolInvocations: b.toolInvocations ?? a.toolInvocations,
                contentBlocks: b.contentBlocks ?? a.contentBlocks,
                recommendations: b.recommendations ?? a.recommendations,
                rating: b.rating ?? a.rating,
                metadata: mergeMetadata(b.metadata, a.metadata),
                artifactId: b.artifactId ?? a.artifactId,
                artifactVersionId: b.artifactVersionId ?? a.artifactVersionId,
            };
        }
        if (!aStreaming && bStreaming) {
            return {
                ...b,
                ...a,
                sources: a.sources ?? b.sources,
                citations: a.citations ?? b.citations,
                toolCalls: a.toolCalls ?? b.toolCalls,
                toolInvocations: a.toolInvocations ?? b.toolInvocations,
                contentBlocks: a.contentBlocks ?? b.contentBlocks,
                recommendations: a.recommendations ?? b.recommendations,
                rating: a.rating ?? b.rating,
                metadata: mergeMetadata(a.metadata, b.metadata),
                artifactId: a.artifactId ?? b.artifactId,
                artifactVersionId: a.artifactVersionId ?? b.artifactVersionId,
            };
        }

        const aContentLen = (a.content ?? "").length;
        const bContentLen = (b.content ?? "").length;
        const preferB = bContentLen > aContentLen;

        const primary = preferB ? b : a;
        const secondary = preferB ? a : b;

        return {
            ...secondary,
            ...primary,
            sources: primary.sources ?? secondary.sources,
            citations: primary.citations ?? secondary.citations,
            toolCalls: primary.toolCalls ?? secondary.toolCalls,
            toolInvocations: primary.toolInvocations ?? secondary.toolInvocations,
            contentBlocks: primary.contentBlocks ?? secondary.contentBlocks,
            recommendations: primary.recommendations ?? secondary.recommendations,
            rating: primary.rating ?? secondary.rating,
            metadata: mergeMetadata(primary.metadata, secondary.metadata),
            artifactId: primary.artifactId ?? secondary.artifactId,
            artifactVersionId: primary.artifactVersionId ?? secondary.artifactVersionId,
        };
    };

    const mergeSessionMessages = (existing: ChatMessage[], incoming: ChatMessage[]) => {
        const byId = new Map<string, ChatMessage>();
        for (const message of existing) {
            if (message?.id) {
                byId.set(message.id, message);
            }
        }
        for (const message of incoming) {
            if (!message?.id) continue;
            const prior = byId.get(message.id);
            const merged = prior ? mergeMessageRecord(prior, message) : message;
            // The server is the only clock that can order a conversation. It
            // separates a user message from its assistant reply by a
            // microsecond, so a locally stamped one sorts unpredictably
            // against them.
            byId.set(
                message.id,
                message.timestamp ? { ...merged, timestamp: message.timestamp } : merged,
            );
        }
        return sortMessagesChronologically([...byId.values()]);
    };

    const refreshSession = useCallback(async (sessionId: string) => {
        if (!sessionId) return;
        try {
            const response = await chatHistoryService.getSession(sessionId);
            const refreshed = mapSessionFromApi(response, true);
            setSessions((prev) => {
                const existing = prev[sessionId];
                const mergedMessages = mergeSessionMessages(existing?.messages ?? [], refreshed.messages ?? []);
                return {
                    ...prev,
                    [sessionId]: {
                        ...existing,
                        ...refreshed,
                        messages: mergedMessages,
                        isPersisted: true,
                        messagesLoaded: true,
                    },
                };
            });
        } catch (error) {
            console.error("Failed to refresh session:", error);
        }
    }, [mapSessionFromApi]);

    const clearSessionRecommendations = useCallback((sessionId: string) => {
        if (!sessionId) return;
        setSessions((prev) => {
            const session = prev[sessionId];
            if (!session) return prev;
            const updatedMessages = session.messages.map((msg) => (
                msg.recommendations ? { ...msg, recommendations: undefined } : msg
            ));
            return {
                ...prev,
                [sessionId]: {
                    ...session,
                    messages: updatedMessages,
                },
            };
        });
    }, []);

    const loadSessionMessages = useCallback(async (sessionId: string) => {
        const session = sessions[sessionId];
        if (!session || session.messagesLoaded) return;
        if (loadingSessionIdRef.current === sessionId) return;

        loadingSessionIdRef.current = sessionId;
        if (activeSessionId === sessionId) {
            setIsActiveSessionLoading(true);
        }

        try {
            const response = await chatHistoryService.getSession(sessionId);
            const loadedSession = mapSessionFromApi(response, true);
            setSessions((prev) => {
                if (!prev[sessionId]) return prev;
                const existingMessages = prev[sessionId]?.messages ?? [];
                const mergedMessages = mergeSessionMessages(existingMessages, loadedSession.messages ?? []);
                return {
                    ...prev,
                    [sessionId]: {
                        ...prev[sessionId],
                        ...loadedSession,
                        messages: mergedMessages,
                        messagesLoaded: true,
                    },
                };
            });
            void artifactService.prefetchProjectArtifacts(sessionId).catch((error) => {
                console.error("Failed to preload report artifacts:", error);
                toast.error("Failed to load report artifacts");
            });
        } catch (error) {
            console.error("Failed to load session messages:", error);
            toast.error("Failed to load chat messages");
        } finally {
            if (loadingSessionIdRef.current === sessionId) {
                loadingSessionIdRef.current = null;
            }
            if (activeSessionId === sessionId) {
                setIsActiveSessionLoading(false);
            }
        }
    }, [activeSessionId, sessions, mapSessionFromApi]);

    const migrateSessionsToBackend = async (localState: ChatSessionsState) => {
        setIsMigrating(true);
        setMigrationMessage("Updating the Hub. Please keep this page open.");

        try {
            await migrateLocalChatSessionsToBackend(localState, safeLocalStorage);
        } finally {
            setIsMigrating(false);
            setMigrationMessage(null);
        }
    };

    // Load from API on mount, migrate local storage if needed
    useEffect(() => {
        let isMounted = true;

        const loadSessions = async () => {
            try {
                const apiResponse = await chatHistoryService.listSessions(false);
                const apiSessions = Array.isArray(apiResponse.sessions)
                    ? apiResponse.sessions.map((session: any) => mapSessionFromApi(session, false))
                    : [];

                if (apiSessions.length > 0) {
                    const sessionMap = apiSessions.reduce((acc, session) => {
                        acc[session.id] = session;
                        return acc;
                    }, {} as Record<string, ChatSession>);

                    if (isMounted) {
                        setSessions(sessionMap);
                        setActiveSessionId(null);
                    }
                } else {
                    const localState = await loadLocalChatSessions(safeLocalStorage);
                    if (localState && Object.keys(localState.sessions || {}).length > 0) {
                        await migrateSessionsToBackend(localState);
                        const refreshed = await chatHistoryService.listSessions(false);
                        const mapped = Array.isArray(refreshed.sessions)
                            ? refreshed.sessions.map((session: any) => mapSessionFromApi(session, false))
                            : [];
                        const sessionMap = mapped.reduce((acc, session) => {
                            acc[session.id] = session;
                            return acc;
                        }, {} as Record<string, ChatSession>);

                        if (isMounted) {
                            setSessions(sessionMap);
                            setActiveSessionId(null);
                        }
                    }
                }

                try {
                    const projectsResponse = await chatProjectsService.listProjects();
                    const mappedProjects = Array.isArray(projectsResponse.projects)
                        ? projectsResponse.projects.map(mapProjectFromApi)
                        : [];
                    const projectMap = mappedProjects.reduce((acc, project) => {
                        acc[project.id] = project;
                        return acc;
                    }, {} as Record<string, AgentProject>);
                    if (isMounted) {
                        setProjects(projectMap);
                    }
                } catch (error) {
                    console.error("Failed to load agent projects:", error);
                    toast.error("Failed to load projects");
                }
            } catch (error) {
                console.error("Failed to load chat sessions:", error);
                toast.error("Failed to load chat history");
            } finally {
                if (isMounted) {
                    setIsLoaded(true);
                }
            }
        };

        void loadSessions();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!activeSessionId) return;
        const session = sessions[activeSessionId];
        if (!session) return;

        if (session.messagesLoaded === false) {
            void loadSessionMessages(activeSessionId);
        } else {
            setIsActiveSessionLoading(false);
        }
    }, [activeSessionId, sessions, loadSessionMessages]);

    // Chat history persistence now lives in the backend database.

    const activeSession = activeSessionId ? sessions[activeSessionId] : null;
    const messages = activeSession?.messages || [];

    const createSession = useCallback((firstMessage?: string, options?: { projectId?: string | null }) => {
        let newSessionId = randomUUID();
        while (sessions[newSessionId]) {
            newSessionId = randomUUID();
        }

        const title = "New Chat";
        const now = new Date().toISOString();

        const newSession: ChatSession = {
            id: newSessionId,
            title,
            projectId: options?.projectId ?? null,
            metadataGeneratedAt: null,
            messages: [],
            createdAt: now,
            lastUpdated: now,
            messagesLoaded: true,
            isPersisted: false,
        };

        setSessions((prev) => ({
            ...prev,
            [newSessionId]: newSession,
        }));
        setActiveSessionId(newSessionId);
        safeLocalStorage.setItem(CHAT_HISTORY_ACTIVE_SESSION_KEY, newSessionId);

        return newSessionId;
    }, [sessions, safeLocalStorage]);

    const ensureSessionPersisted = useCallback(async (sessionId: string, title: string, projectId: string | null) => {
        const pending = pendingSessionCreatesRef.current.get(sessionId);
        if (pending) {
            await pending;
            return;
        }

        const createPromise = chatHistoryService.createSession({
            id: sessionId,
            title,
            project_id: projectId,
        });
        pendingSessionCreatesRef.current.set(sessionId, createPromise);

        try {
            const created = await createPromise;
            const normalizedCreatedAt = normalizeApiInstantString(created?.created_at);
            const normalizedUpdatedAt = normalizeApiInstantString(created?.updated_at);
            setSessions((prev) => {
                const session = prev[sessionId];
                if (!session) return prev;
                return {
                    ...prev,
                    [sessionId]: {
                        ...session,
                        title: created?.title ?? session.title,
                        createdAt: normalizedCreatedAt ?? session.createdAt,
                        lastUpdated: normalizedUpdatedAt ?? session.lastUpdated,
                        isPersisted: true,
                    },
                };
            });
        } catch (error) {
            console.error("Failed to create session:", error);
            throw error;
        } finally {
            pendingSessionCreatesRef.current.delete(sessionId);
        }
    }, []);

    const createPersistedSession = useCallback(async (options?: { projectId?: string | null; title?: string }) => {
        const title = options?.title?.trim() || "New Chat";
        const projectId = options?.projectId ?? null;
        const sessionId = createSession(undefined, { projectId });
        await ensureSessionPersisted(sessionId, title, projectId);
        return sessionId;
    }, [createSession, ensureSessionPersisted]);

    const switchSession = useCallback((sessionId: string) => {
        if (sessions[sessionId]) {
            setActiveSessionId(sessionId);
            safeLocalStorage.setItem(CHAT_HISTORY_ACTIVE_SESSION_KEY, sessionId);
            if (sessions[sessionId].messagesLoaded === false) {
                setIsActiveSessionLoading(true);
            }
        } else {
            console.warn("Session not found:", sessionId);
        }
    }, [sessions, safeLocalStorage]);

    const renameSession = useCallback((sessionId: string, title: string) => {
        const trimmed = title.trim();
        if (!trimmed) return;
        if (!sessions[sessionId]) {
            console.warn("Session not found:", sessionId);
            return;
        }

        const previousTitle = sessions[sessionId]?.title ?? "New Chat";
        if (trimmed === previousTitle) return;
        setSessions((prev) => {
            const session = prev[sessionId];
            if (!session) return prev;
            return {
                ...prev,
                [sessionId]: {
                    ...session,
                    title: trimmed,
                    lastUpdated: new Date().toISOString(),
                },
            };
        });

        const pendingCreate = pendingSessionCreatesRef.current.get(sessionId);
        const isPersisted = sessions[sessionId]?.isPersisted ?? Boolean(pendingCreate);

        void (async () => {
            try {
                if (pendingCreate) {
                    await pendingCreate;
                    await chatHistoryService.updateSession(sessionId, { title: trimmed });
                    return;
                }
                if (!isPersisted) {
                    await ensureSessionPersisted(sessionId, trimmed, sessions[sessionId]?.projectId ?? null);
                    return;
                }
                await chatHistoryService.updateSession(sessionId, { title: trimmed });
            } catch (error) {
                console.error("Failed to rename session:", error);
                toast.error("Failed to rename chat");
                setSessions((prev) => {
                    const session = prev[sessionId];
                    if (!session) return prev;
                    return {
                        ...prev,
                        [sessionId]: {
                            ...session,
                            title: previousTitle,
                        },
                    };
                });
            }
        })();
    }, [ensureSessionPersisted, sessions]);

    const deleteSession = useCallback((sessionId: string) => {
        const pendingCreate = pendingSessionCreatesRef.current.get(sessionId);
        const isPersisted = sessions[sessionId]?.isPersisted ?? Boolean(pendingCreate);
        setSessions((prev) => {
            const newSessions = { ...prev };
            delete newSessions[sessionId];
            return newSessions;
        });

        const remainingSessions = Object.keys(sessions).filter(id => id !== sessionId);
        if (sessionId === activeSessionId) {
            const nextActive = remainingSessions[0] ?? null;
            setActiveSessionId(nextActive);
            if (nextActive) {
                safeLocalStorage.setItem(CHAT_HISTORY_ACTIVE_SESSION_KEY, nextActive);
            } else {
                safeLocalStorage.removeItem(CHAT_HISTORY_ACTIVE_SESSION_KEY);
            }
        }

        if (pendingCreate) {
            pendingSessionCreatesRef.current.delete(sessionId);
            void pendingCreate
                .then(() => chatHistoryService.deleteSession(sessionId))
                .catch((error) => {
                    console.error("Failed to delete session after creation:", error);
                });
        } else if (isPersisted) {
            void chatHistoryService.deleteSession(sessionId).catch((error) => {
                console.error("Failed to delete session:", error);
                toast.error("Failed to delete chat session");
            });
        }
    }, [activeSessionId, sessions, safeLocalStorage]);

    const addMessage = useCallback(
        (message: Omit<ChatMessage, "id" | "timestamp">, sessionIdOverride?: string) => {
            let sessionId = sessionIdOverride ?? activeSessionId;
            let createdDraft = false;
            if (!sessionId) {
                sessionId = randomUUID();
                const title = "New Chat";
                const now = new Date().toISOString();
                const newSession: ChatSession = {
                    id: sessionId,
                    title,
                    projectId: null,
                    metadataGeneratedAt: null,
                    messages: [],
                    createdAt: now,
                    lastUpdated: now,
                    messagesLoaded: true,
                    isPersisted: false,
                };
                setSessions((prev) => ({
                    ...prev,
                    [sessionId!]: newSession,
                }));
                createdDraft = true;
                setActiveSessionId(sessionId);
                safeLocalStorage.setItem(CHAT_HISTORY_ACTIVE_SESSION_KEY, sessionId);
            }

            const newMessage: ChatMessage = {
                ...message,
                id: randomUUID(),
                timestamp: new Date().toISOString(),
            };

            setSessions((prev) => {
                const existingSession = prev[sessionId!];
                const session = existingSession ?? {
                    id: sessionId!,
                    title: "New Chat",
                    projectId: null,
                    metadataGeneratedAt: null,
                    messages: [],
                    createdAt: new Date().toISOString(),
                    lastUpdated: new Date().toISOString(),
                    messagesLoaded: true,
                    isPersisted: false,
                };

                return {
                    ...prev,
                    [sessionId!]: {
                        ...session,
                        title: session.title,
                        messages: [...session.messages, newMessage].slice(-MAX_MESSAGES_PER_SESSION),
                        lastUpdated: new Date().toISOString(),
                        messagesLoaded: true,
                    },
                };
            });

            const payload = {
                id: newMessage.id,
                role: newMessage.role,
                content: newMessage.content,
                timestamp: newMessage.timestamp,
                sources: newMessage.sources ?? null,
                citations: newMessage.citations ?? null,
                tool_calls: newMessage.toolCalls ?? null,
                tool_invocations: newMessage.toolInvocations ?? null,
                content_blocks: newMessage.contentBlocks ?? null,
                recommendations: newMessage.recommendations ?? null,
                rating: newMessage.rating ?? null,
                metadata: newMessage.metadata ?? null,
                artifact_id: newMessage.artifactId ?? null,
                artifact_version_id: newMessage.artifactVersionId ?? null,
            };

            const existingSession = sessions[sessionId!];
            const isPersisted = existingSession?.isPersisted ?? !createdDraft;
            const newTitle = existingSession?.title ?? "New Chat";

            void (async () => {
                try {
                    if (!isPersisted) {
                        await ensureSessionPersisted(sessionId!, newTitle, existingSession?.projectId ?? null);
                    }
                    await chatHistoryService.addMessage(sessionId!, payload);
                } catch (error) {
                    console.error("Failed to persist message:", error);
                    toast.error("Failed to save chat message");
                    return;
                }

                // Title generation now handled by backend metadata service.
            })();
            return sessionId!;
        },
        [activeSessionId, ensureSessionPersisted, sessions, safeLocalStorage]
    );

    const updateMessage = useCallback((messageId: string, updates: Partial<ChatMessage>) => {
        if (!activeSessionId) return;
        const isPersisted = sessions[activeSessionId]?.isPersisted ?? true;

        setSessions((prev) => {
            const session = prev[activeSessionId];
            if (!session) return prev;

            const updatedMessages = session.messages.map(msg => {
                if (msg.id === messageId) {
                    return { ...msg, ...updates };
                }
                return msg;
            });

            return {
                ...prev,
                [activeSessionId]: {
                    ...session,
                    messages: updatedMessages,
                    lastUpdated: new Date().toISOString(),
                },
            };
        });

        const payload: Record<string, any> = {};
        if (updates.content !== undefined) payload.content = updates.content;
        if (updates.sources !== undefined) payload.sources = updates.sources ?? null;
        if (updates.citations !== undefined) payload.citations = updates.citations ?? null;
        if (updates.toolCalls !== undefined) payload.tool_calls = updates.toolCalls ?? null;
        if (updates.toolInvocations !== undefined) payload.tool_invocations = updates.toolInvocations ?? null;
        if (updates.contentBlocks !== undefined) payload.content_blocks = updates.contentBlocks ?? null;
        if (updates.recommendations !== undefined) payload.recommendations = updates.recommendations ?? null;
        if (updates.rating !== undefined) payload.rating = updates.rating ?? null;
        if (updates.metadata !== undefined) payload.metadata = updates.metadata ?? null;
        if (updates.artifactId !== undefined) payload.artifact_id = updates.artifactId ?? null;
        if (updates.artifactVersionId !== undefined) payload.artifact_version_id = updates.artifactVersionId ?? null;

        if (isPersisted && Object.keys(payload).length > 0) {
            void chatHistoryService.updateMessage(activeSessionId, messageId, payload).catch((error) => {
                console.error("Failed to update message:", error);
            });
        }
    }, [activeSessionId, sessions]);

    const upsertMessageLocal = useCallback((sessionId: string, message: ChatMessage) => {
        if (!sessionId) return;

        setSessions((prev) => {
            const existingSession = prev[sessionId];
            const now = new Date().toISOString();
            const ensuredSession: ChatSession = existingSession ?? {
                id: sessionId,
                title: "New Chat",
                projectId: null,
                metadataGeneratedAt: null,
                messages: [],
                createdAt: now,
                lastUpdated: now,
                messagesLoaded: true,
                // Backend-created messages imply the session exists server-side.
                isPersisted: true,
            };

            const existingMessages = ensuredSession.messages || [];
            const existingIndex = existingMessages.findIndex((msg) => msg.id === message.id);
            const nextMessages = existingIndex >= 0
                ? existingMessages.map((msg, idx) => (idx === existingIndex ? { ...msg, ...message } : msg))
                : [...existingMessages, message].slice(-MAX_MESSAGES_PER_SESSION);

            return {
                ...prev,
                [sessionId]: {
                    ...ensuredSession,
                    isPersisted: true,
                    messages: sortMessagesChronologically(nextMessages),
                    lastUpdated: now,
                    messagesLoaded: true,
                },
            };
        });
    }, [sortMessagesChronologically]);

    const clearCurrentSession = useCallback(() => {
        if (!activeSessionId) return;
        const isPersisted = sessions[activeSessionId]?.isPersisted ?? true;

        setSessions((prev) => ({
            ...prev,
            [activeSessionId]: {
                ...prev[activeSessionId],
                messages: [],
                lastUpdated: new Date().toISOString(),
                messagesLoaded: true,
            },
        }));

        if (isPersisted) {
            void chatHistoryService.clearSessionMessages(activeSessionId).catch((error) => {
                console.error("Failed to clear session messages:", error);
                toast.error("Failed to clear chat session");
            });
        }
    }, [activeSessionId, sessions]);

    const importSession = useCallback((importedMessages: Omit<ChatMessage, "id" | "timestamp">[]) => {
        if (importedMessages.length === 0) return;

        const firstUserMessage = importedMessages.find((m) => m.role === "user");
        const title = firstUserMessage
            ? generateSessionTitle(firstUserMessage.content)
            : "Imported Chat";

        const now = new Date().toISOString();
        const fullMessages: ChatMessage[] = importedMessages.map((msg) => ({
            ...msg,
            id: randomUUID(),
            timestamp: new Date().toISOString(),
        }));

        const currentSession = activeSessionId ? sessions[activeSessionId] : null;
        const isCurrentEmpty = !currentSession || (currentSession.messagesLoaded && currentSession.messages.length === 0);
        const targetSessionId = isCurrentEmpty && activeSessionId
            ? activeSessionId
            : randomUUID();

        const targetSession: ChatSession = {
            id: targetSessionId,
            title,
            messages: fullMessages,
            createdAt: now,
            lastUpdated: now,
            messagesLoaded: true,
            isPersisted: true,
            metadataGeneratedAt: new Date().toISOString(),
        };

        setSessions((prev) => ({
            ...prev,
            [targetSessionId]: targetSession,
        }));
        setActiveSessionId(targetSessionId);
        safeLocalStorage.setItem(CHAT_HISTORY_ACTIVE_SESSION_KEY, targetSessionId);

        void chatHistoryService.bulkImport({
            sessions: [
                {
                    id: targetSessionId,
                    title,
                    created_at: now,
                    updated_at: now,
                    messages: fullMessages.map((message) => ({
                        id: message.id,
                        role: message.role,
                        content: message.content,
                        timestamp: message.timestamp,
                        sources: message.sources ?? null,
                        citations: message.citations ?? null,
                        tool_calls: message.toolCalls ?? null,
                        tool_invocations: message.toolInvocations ?? null,
                        content_blocks: message.contentBlocks ?? null,
                        recommendations: message.recommendations ?? null,
                        rating: message.rating ?? null,
                        metadata: message.metadata ?? null,
                        artifact_id: message.artifactId ?? null,
                        artifact_version_id: message.artifactVersionId ?? null,
                    })),
                },
            ],
        }).catch((error) => {
            console.error("Failed to import chat session:", error);
            toast.error("Failed to import chat session");
        });
    }, [activeSessionId, sessions, safeLocalStorage]);

    const sortedSessions = Object.values(sessions)
        .filter((session) => session.id === activeSessionId || session.messagesLoaded === false || session.messages.length > 0)
        .sort(
            (a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
        );

    const sortedProjects = useMemo(() => (
        Object.values(projects).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    ), [projects]);

    const createProject = useCallback(async (name: string) => {
        const trimmed = name.trim();
        if (!trimmed) {
            throw new Error("Project name cannot be empty");
        }
        const created = await chatProjectsService.createProject({ name: trimmed });
        const mapped = mapProjectFromApi(created);
        setProjects((prev) => ({ ...prev, [mapped.id]: mapped }));
        return mapped;
    }, [mapProjectFromApi]);

    const renameProject = useCallback(async (projectId: string, name: string) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        const updated = await chatProjectsService.updateProject(projectId, { name: trimmed });
        const mapped = mapProjectFromApi(updated);
        setProjects((prev) => ({ ...prev, [mapped.id]: mapped }));
    }, [mapProjectFromApi]);

    const deleteProject = useCallback(async (projectId: string) => {
        await chatProjectsService.deleteProject(projectId);
        setProjects((prev) => {
            const next = { ...prev };
            delete next[projectId];
            return next;
        });
        setSessions((prev) => {
            const next: Record<string, ChatSession> = {};
            for (const [id, session] of Object.entries(prev)) {
                next[id] = session.projectId === projectId ? { ...session, projectId: null } : session;
            }
            return next;
        });
    }, []);

    const moveSessionToProject = useCallback(async (sessionId: string, projectId: string | null) => {
        const session = sessions[sessionId];
        if (!session) {
            console.warn("Session not found:", sessionId);
            return;
        }

        const previousProjectId = session.projectId;
        if (projectId === previousProjectId) return;

        // Update local state first
        setSessions((prev) => ({
            ...prev,
            [sessionId]: {
                ...prev[sessionId],
                projectId: projectId,
                lastUpdated: new Date().toISOString(),
            },
        }));

        const pendingCreate = pendingSessionCreatesRef.current.get(sessionId);
        const isPersisted = session.isPersisted ?? Boolean(pendingCreate);

        try {
            if (pendingCreate) {
                await pendingCreate;
                await chatHistoryService.updateSession(sessionId, { project_id: projectId });
                return;
            }
            if (!isPersisted) {
                await ensureSessionPersisted(sessionId, session.title, projectId);
                return;
            }
            await chatHistoryService.updateSession(sessionId, { project_id: projectId });
        } catch (error) {
            console.error("Failed to move session to project:", error);
            toast.error("Failed to move chat to project");
            // Revert on error
            setSessions((prev) => ({
                ...prev,
                [sessionId]: {
                    ...prev[sessionId],
                    projectId: previousProjectId,
                },
            }));
        }
    }, [ensureSessionPersisted, sessions]);

    useEffect(() => {
        if (activeSession && activeSession.messages) {
            const unique = new Set();
            const hasDuplicates = activeSession.messages.some(m => {
                if (unique.has(m.id)) return true;
                unique.add(m.id);
                return false;
            });

            if (hasDuplicates) {
                setSessions(prev => {
                    const session = prev[activeSession.id];
                    if (!session) return prev;

                    const uniqueMsgs = session.messages.filter((msg, index, self) =>
                        index === self.findIndex(m => m.id === msg.id)
                    );

                    return {
                        ...prev,
                        [activeSession.id]: {
                            ...session,
                            messages: uniqueMsgs
                        }
                    };
                });
            }
        }
    }, [activeSession]);

    return (
        <ChatHistoryContext.Provider
            value={{
                messages,
                activeSessionId,
                activeSession,
                sessions,
                sortedSessions,
                projects,
                sortedProjects,
                addMessage,
                updateMessage,
                upsertMessageLocal,
                createSession,
                createPersistedSession,
                ensureSessionPersisted,
                switchSession,
                renameSession,
                deleteSession,
                clearCurrentSession,
                importSession,
                refreshSession,
                clearSessionRecommendations,
                createProject,
                renameProject,
                deleteProject,
                moveSessionToProject,
                isLoaded,
                isActiveSessionLoading,
                isMigrating,
                migrationMessage,
            }}
        >
            {children}
        </ChatHistoryContext.Provider>
    );
};

export const useChatHistoryContext = () => {
    const context = useContext(ChatHistoryContext);
    if (context === undefined) {
        throw new Error("useChatHistoryContext must be used within a ChatHistoryProvider");
    }
    return context;
};
